<template>
  <div class="min-h-screen bg-gray-50 flex flex-col items-center py-8">
    <!-- Add Newspaper Button -->
    <div class="w-full max-w-4xl mb-6">
      <button
        @click="openModal('add')"
        class="py-2.5 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-md transition"
      >
        📰 Add Newspaper
      </button>
    </div>

    <!-- Modal for Upload/Update Form -->
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="closeModal"
    >
      <div
        class="bg-white shadow-lg rounded-2xl w-full max-w-lg p-8 border border-gray-200"
      >
        <h2 class="text-2xl font-semibold text-gray-800 mb-6 text-center">
          {{
            modalMode === "add"
              ? "📰 Upload Monthly Newspaper"
              : "📝 Update Newspaper"
          }}
        </h2>

        <form
          @submit.prevent="
            modalMode === 'add' ? uploadFiles() : updateNewspaper()
          "
          class="space-y-5"
        >
          <!-- Title -->
          <div>
            <label class="block text-gray-700 font-medium mb-2">Title</label>
            <input
              type="text"
              v-model="title"
              placeholder="Enter newspaper title"
              required
              class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
            />
          </div>

          <!-- Description -->
          <div>
            <label class="block text-gray-700 font-medium mb-2"
              >Description</label
            >
            <textarea
              v-model="description"
              placeholder="Enter a short description"
              rows="3"
              class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none"
            ></textarea>
          </div>
          <div>
            <label for="pdfInput" class="block text-gray-700 font-medium mb-2">
              Select PDF File (Required)
            </label>
            <input
              id="pdfInput"
              ref="pdfInput"
              type="file"
              @change="onPDFChange"
              accept="application/pdf"
              required
              class="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-red-100 file:text-red-800 hover:file:bg-red-200 cursor-pointer"
            />
            <p v-if="pdfFile" class="text-sm text-gray-500 mt-1">
              Selected: {{ pdfFile.name }}
            </p>
          </div>
          <!-- Image Upload -->
          <div>
            <label
              for="imageInput"
              class="block text-gray-700 font-medium mb-2"
            >
              Select Cover Image
            </label>
            <input
              id="imageInput"
              ref="imageInput"
              type="file"
              @change="onImageChange"
              accept="image/*"
              class="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-yellow-100 file:text-yellow-800 hover:file:bg-yellow-200 cursor-pointer"
            />
            <div v-if="imagePreview" class="mt-3 flex flex-col items-center">
              <img
                :src="imagePreview"
                alt="Cover Preview"
                class="w-32 h-32 object-cover rounded-lg border shadow-sm"
              />
              <p class="text-sm text-gray-500 mt-1">Preview Image</p>
            </div>
          </div>
          <!-- is_paid Toggle -->
          <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
            <div>
              <p class="text-gray-700 font-medium">Paid Issue</p>
              <p class="text-xs text-gray-500">Toggle off to make this newspaper free to download</p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" v-model="is_paid" class="sr-only peer" />
              <div class="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-green-500 peer-focus:ring-2 peer-focus:ring-green-400 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
              <span class="ml-3 text-sm font-semibold" :class="is_paid ? 'text-green-600' : 'text-gray-500'">
                {{ is_paid ? '💰 Paid' : '🆓 Free' }}
              </span>
            </label>
          </div>


          <div class="flex space-x-4">
            <button
              type="submit"
              :disabled="loading"
              class="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-md transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span v-if="!loading">{{
                modalMode === "add" ? "📤 Upload Files" : "💾 Update Newspaper"
              }}</span>
              <span v-else
                >⏳
                {{ modalMode === "add" ? "Uploading..." : "Updating..." }}</span
              >
            </button>
            <button
              type="button"
              @click="closeModal"
              class="w-full py-2.5 bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold rounded-lg shadow-md transition"
            >
              Cancel
            </button>
          </div>
        </form>

        <!-- Status Messages -->
        <div v-if="success" class="text-center mt-4 text-green-600 font-medium">
          ✅ {{ success }}
        </div>
        <div v-if="error" class="text-center mt-4 text-red-600 font-medium">
          ❌ {{ error }}
        </div>
      </div>
    </div>

    <!-- Newspapers Table -->
    <div
      class="bg-white shadow-lg rounded-2xl w-full max-w-4xl p-8 border border-gray-200"
    >
      <h2 class="text-2xl font-semibold text-gray-800 mb-6 text-center">
        🗞️ All Newspapers
      </h2>
      <div v-if="loadingNewspapers" class="text-center text-gray-600">
        ⏳ Loading newspapers...
      </div>
      <div
        v-else-if="newspapers.length === 0"
        class="text-center text-gray-600"
      >
        No newspapers found.
      </div>
      <table v-else class="w-full table-auto border-collapse">
        <thead>
          <tr class="bg-gray-100">
            <th class="px-4 py-2 text-left text-gray-700 font-medium">Title</th>
            <th class="px-4 py-2 text-left text-gray-700 font-medium">Description</th>
            <th class="px-4 py-2 text-left text-gray-700 font-medium">Access</th>
            <th class="px-4 py-2 text-left text-gray-700 font-medium">PDF</th>
            <th class="px-4 py-2 text-left text-gray-700 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="newspaper in newspapers"
            :key="newspaper.id"
            class="border-b hover:bg-gray-50"
          >
            <td class="px-4 py-2">{{ newspaper.title }}</td>
            <td class="px-4 py-2">{{ newspaper.description || 'No description' }}</td>
            <!-- Access badge -->
            <td class="px-4 py-2">
              <span
                :class="newspaper.is_paid ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-700'"
                class="px-2 py-1 rounded-full text-xs font-semibold"
              >
                {{ newspaper.is_paid ? '💰 Paid' : '🆓 Free' }}
              </span>
            </td>
            <td class="px-4 py-2">
              <a :href="newspaper.pdf_path" target="_blank" class="text-blue-600 hover:underline">
                View PDF
              </a>
            </td>
            <td class="px-4 py-2 flex gap-2 flex-wrap">
              <!-- Free / Paid toggle -->
              <button
                @click="togglePaid(newspaper)"
                :class="newspaper.is_paid ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-500 hover:bg-amber-600'"
                class="px-3 py-1 text-white rounded-lg text-xs transition"
              >
                {{ newspaper.is_paid ? '🆓 Make Free' : '💰 Make Paid' }}
              </button>
              <button
                @click="openModal('update', newspaper)"
                class="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition text-xs"
              >
                ✏️ Edit
              </button>
              <button
                @click="confirmDelete(newspaper.id)"
                class="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded-lg transition text-xs"
              >
                🗑️ Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from 'sweetalert2';

export default {
  name: "UploadNewspaper",
  data() {
    return {
      title: "",
      description: "",
      is_paid: true,           // ← default NEW uploads to paid
      pdfFile: null,
      imageFile: null,
      imagePreview: null,
      loading: false,
      success: "",
      error: "",
      newspapers: [],
      loadingNewspapers: false,
      showModal: false,
      modalMode: "add",
      selectedNewspaperId: null,
    };
  },
  methods: {
    onPDFChange(e) {
      this.pdfFile = e.target.files[0];
    },
    onImageChange(e) {
      this.imageFile = e.target.files[0];
      this.imagePreview = URL.createObjectURL(this.imageFile);
    },

    /** ---------------------------
     * ADD NEW NEWSPAPER (POST)
     * --------------------------- */
    async uploadFiles() {
      if (!this.pdfFile) {
        this.error = "Please select a PDF file.";
        return;
      }

      this.loading = true;
      this.error = "";
      this.success = "";

      try {
        const formData = new FormData();
        formData.append("title", this.title);
        formData.append("description", this.description);
        formData.append("is_paid", this.is_paid ? 1 : 0);
        formData.append("pdfFile", this.pdfFile);
        if (this.imageFile) formData.append("imageFile", this.imageFile);

        const res = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/upload-pdf`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        this.success = res.data.message || "Newspaper added successfully!";
        this.resetForm();
        await this.fetchNewspapers();
        this.closeModal();
      } catch (err) {
        this.error = err.response?.data?.error || "Upload failed.";
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    /** ---------------------------
     * UPDATE NEWSPAPER (PUT)
     * --------------------------- */
    async updateNewspaper() {
      this.loading = true;
      this.error = "";
      this.success = "";

      try {
        const formData = new FormData();
        formData.append("title", this.title);
        formData.append("description", this.description);
        if (this.pdfFile) formData.append("pdfFile", this.pdfFile);
        if (this.imageFile) formData.append("imageFile", this.imageFile);

        const res = await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/newspapers/${this.selectedNewspaperId}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        this.success = res.data.message || "Newspaper updated successfully!";
        this.resetForm();
        await this.fetchNewspapers();
        this.closeModal();
      } catch (err) {
        this.error = err.response?.data?.error || "Update failed.";
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    /** DELETE */
    async deleteNewspaper(id) {
      try {
        await axios.delete(
          `${process.env.VUE_APP_API_BASE_URL}/newspapers/${id}`
        );
        // this.success = res.data.message; // Optional: keep or remove if using Swal
        await this.fetchNewspapers();
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Newspaper deleted successfully.',
          confirmButtonColor: '#BF3131'
        });
      } catch (err) {
        // this.error = "Failed to delete newspaper.";
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete newspaper.',
          confirmButtonColor: '#BF3131'
        });
      }
    },

    async confirmDelete(id) {
      const result = await Swal.fire({
        title: 'Delete Newspaper?',
        text: "Are you sure you want to delete this newspaper?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        await this.deleteNewspaper(id);
      }
    },
    /** OPEN & CLOSE MODAL */
    openModal(mode, newspaper = null) {
      this.modalMode = mode;
      this.success = '';
      this.error = '';
      if (mode === 'update' && newspaper) {
        this.title = newspaper.title;
        this.description = newspaper.description || '';
        this.is_paid = !!newspaper.is_paid;
        this.selectedNewspaperId = newspaper.id;
      } else {
        this.title = '';
        this.description = '';
        this.is_paid = true; // default new = paid
        this.selectedNewspaperId = null;
      }
      this.pdfFile = null;
      this.imageFile = null;
      this.imagePreview = null;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },

    /** FETCH all newspapers */
    async fetchNewspapers() {
      this.loadingNewspapers = true;
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/newspapers`);
        this.newspapers = res.data;
      } catch (err) {
        console.error('Failed to fetch newspapers:', err);
      } finally {
        this.loadingNewspapers = false;
      }
    },

    /** TOGGLE FREE / PAID */
    async togglePaid(newspaper) {
      const action = newspaper.is_paid ? 'make free' : 'make paid';
      const result = await Swal.fire({
        title: newspaper.is_paid ? 'Make Free?' : 'Make Paid?',
        text: `Are you sure you want to ${action} "${newspaper.title}"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: newspaper.is_paid ? '#10B981' : '#F59E0B',
        cancelButtonColor: '#6B7280',
        confirmButtonText: `Yes, ${action}!`
      });
      if (!result.isConfirmed) return;

      try {
        const res = await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/admin/newspapers/${newspaper.id}/toggle-paid`
        );
        // Update local state instantly
        const idx = this.newspapers.findIndex(n => n.id === newspaper.id);
        if (idx !== -1) this.newspapers[idx].is_paid = res.data.is_paid;

        Swal.fire({
          icon: 'success',
          title: res.data.is_paid ? 'Now Paid 💰' : 'Now Free 🆓',
          text: res.data.message,
          confirmButtonColor: '#BF3131',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to toggle.', confirmButtonColor: '#BF3131' });
      }
    },
  },
  mounted() {
    this.fetchNewspapers();
  },
};
</script>

<style scoped>
button,
input,
textarea {
  transition: all 0.2s ease;
}
</style>
